import styled from '@emotion/styled'
import {
    ApiFullLandingPageLink,
    INLINE_CTA_ACTION,
} from '@storefronts/common-types'
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import { useBenefitsCarousel } from '../../../hooks/useBenefitsCarousel'
import { useEscapeToDismiss } from '../../../hooks/useEscapeToDismiss'
import { useShopPayButtons } from '../../../hooks/useShopPayButtons'
import { EL_USER_DATA, EL_VIEW_ITEM } from '../../../analytics/elevar'
import { MetaPixelTracker } from '../../../analytics/meta'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { CartContext } from '../../../context/cartContext'
import {
    CustomizationContext,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    SECONDARY_CTA_BORDER_VAR,
    SUBTLE_BORDER_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { PageContext } from '../../../context/PageContextProvider'
import { ProductContext } from '../../../context/productContext'
import { QueryParamContext } from '../../../context/QueryParamContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { useOnScreen } from '../../../hooks/useOnScreen'
import { useFullLandingPageLink } from '../../../hooks/useFullLandingPageLink'
import {
    getStickyFooterSafeAreaPadding,
    STICKY_FOOTER_BENEFITS_CAROUSEL_PADDING_BOTTOM,
    STICKY_FOOTER_BUTTON_AREA_PADDING_BOTTOM,
    STICKY_FOOTER_BUTTON_GAP,
    STICKY_FOOTER_INNER_PADDING,
    STICKY_FOOTER_SHADOW,
} from '../../../style/styleConstants'
import {
    inScrollingLayout,
    scaledFontSize,
    ScrollingLayout,
    SECTION_CONTAINER_NAME,
} from '../../../style/scrollingLayout'
import { buildCartVariant } from '../../../utils/cartTools'
import { addRecentlyViewedToSession } from '../../../storage'
import {
    getCartActionBrandBenefits,
    hasStickyFooterBrandBenefits,
} from '../../../utils/stickyFooterUtils'
import { AttentiveTracker } from '../../pixels/AttentivePixel'
import { BlotoutTrackker } from '../../pixels/BlotoutPixel'
import { ConvergeTracker } from '../../pixels/ConvergePixel'
import { PostscriptTracker } from '../../pixels/PostscriptPixel'
import {
    BenefitsCarouselViewport,
    BenefitSlide,
    BenefitSlideContent,
} from '../../shared/BenefitsCarousel'
import { ProductContent, useProductData } from '../products/ProductContent'
import { ProductDrawerCTA } from './ProductDrawerCTA'
import { CartIcon } from '../CartIcon'
import { FloatingCart } from '../FloatingCart'
import { DrawerHeader } from './DrawerHeader'
import { FullScreenContainer } from './FullScreenContainer'
import {
    overlayModalCloseClearance,
    overlayModalPanel,
    overlayModalScrim,
} from '../../shared/overlayModal'
import { useHeaderScrollVisibility } from '../../../hooks/useHeaderScrollVisibility'
import { useDesktopModal } from '../../../hooks/useDesktopModal'
import { OverlayCloseButton } from '../../shared/OverlayCloseButton'

const StickyFooterWrapper = styled.div<{ hasBrandBenefits: boolean }>(
    ({ hasBrandBenefits }) => ({
        position: 'sticky',
        bottom: 0,
        width: '100%',
        backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
        borderTop: `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
        boxShadow: STICKY_FOOTER_SHADOW,
        paddingBottom: getStickyFooterSafeAreaPadding(hasBrandBenefits),
    }),
)

const FooterContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    fontSize: scaledFontSize(14),
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    // The bar spans the overlay; a button that wide reads as a banner, so the
    // buttons keep the measure the page's own CTA keeps.
    ...inScrollingLayout({
        maxWidth: ScrollingLayout.CTA_MAX_WIDTH,
        marginInline: 'auto',
    }),
})

const DrawerContainer = styled.div<{ inline: boolean; modal?: boolean }>(
    ({ inline, modal = false }) => ({
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...(inline
            ? { height: 'fit-content' }
            : {
                  height: '100%',
                  // Lets the box shrink past its content inside the modal
                  // panel, which is what turns overflow into a scroll.
                  minHeight: 0,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                      display: 'none',
                  },
              }),
        ...(modal ? overlayModalCloseClearance : null),
    }),
)

export const InlineProductPageContent: React.FC<{
    lpLink: ApiFullLandingPageLink
    showBodyContent?: boolean
    showReviews?: boolean
    inlineCTAActions?: INLINE_CTA_ACTION[]
    inlineButtonText?: string
    prioritizeHeroImage?: boolean
    bodyBadgesOverride?: string[]
    /** @see ProductContent.flushContent */
    flushContent?: boolean
    /** @see ProductContent.singleColumn */
    singleColumn?: boolean
    /** @see ProductContent.embedded */
    embedded?: boolean
    /** @see ProductContent.showBrandChat */
    showBrandChat?: boolean
}> = ({
    lpLink,
    showBodyContent = false,
    showReviews = false,
    inlineCTAActions = [INLINE_CTA_ACTION.ADD_TO_CART],
    inlineButtonText,
    prioritizeHeroImage = true,
    bodyBadgesOverride,
    flushContent = false,
    singleColumn = false,
    embedded = false,
    showBrandChat = false,
}) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { HIDE_CART_OVERRIDE } = useContext(QueryParamContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { setActiveLPLink } = useContext(PageContext)

    const {
        activeVariant,
        setActiveVariant,
        reviewsRef,
        price,
        comparePrice,
        discountPercentage,
        selectedSellingPlanId,
        setSelectedSellingPlanId,
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    } = useProductData(lpLink, landingPage)

    const productContainerRef = useRef<HTMLDivElement>(null)

    const onScreen = useOnScreen(productContainerRef)

    useEffect(() => {
        if (onScreen && activeVariant) {
            Analytics.TRACK_PRODUCT_SCROLL_VIEW_CONTENT(lpLink, activeVariant)
        }
    }, [onScreen, activeVariant, lpLink.linkHash, Analytics])

    useEffect(() => {
        // Tracking stuff
        if (activeVariant) {
            EL_VIEW_ITEM(lpLink, activeVariant)
            AttentiveTracker.trackProductView(lpLink, activeVariant)
            PostscriptTracker.trackProductView(lpLink, activeVariant)
            BlotoutTrackker.trackProductView(lpLink, activeVariant)
            ConvergeTracker.trackProductView(lpLink, activeVariant)
            MetaPixelTracker.trackProductView(
                landingPage.client.clientHash,
                lpLink,
            )
        }
    }, [activeVariant])

    return (
        <ProductContext.Provider value={{ reviewsRef }}>
            <DrawerContainer inline={true} ref={productContainerRef}>
                <ProductContent
                    // An embedded product does not scroll — the page does — so
                    // it must not open a scroll box of its own, which would
                    // clip any section inside it that bleeds past the column.
                    // No scroll target here: whichever ancestor scrolls owns
                    // the scroll, and we cannot reliably grab a ref to it.
                    isInOuterScrollContainer={true}
                    embedded={embedded}
                    lpLink={lpLink}
                    activeVariant={activeVariant}
                    setActiveVariant={setActiveVariant}
                    reviewsRef={reviewsRef}
                    price={price}
                    comparePrice={comparePrice}
                    discountPercentage={discountPercentage}
                    hideCartOverride={HIDE_CART_OVERRIDE}
                    showBodyContent={showBodyContent}
                    showReviews={showReviews}
                    renderCTAInline={true}
                    inlineCTAActions={inlineCTAActions}
                    inlineButtonText={inlineButtonText}
                    selectedSellingPlanId={selectedSellingPlanId}
                    onSellingPlanSelect={setSelectedSellingPlanId}
                    displayPoweredByFooter={false}
                    prioritizeHeroImage={prioritizeHeroImage}
                    bodyBadgesOverride={bodyBadgesOverride}
                    flushContent={flushContent}
                    singleColumn={singleColumn}
                    showBrandChat={showBrandChat}
                    onTitleClick={
                        !showBodyContent
                            ? () =>
                                  setActiveLPLink(
                                      lpLink,
                                      undefined,
                                      bodyBadgesOverride,
                                  )
                            : undefined
                    }
                />
            </DrawerContainer>
        </ProductContext.Provider>
    )
}

export const ProductPage: React.FC<{
    lpLink: ApiFullLandingPageLink
    inline: boolean
    /** When opening full PDP from PLP (see ActiveLPLinkOptions) */
    initialVariantShopifyId?: string | null
    bodyBadgesOverride?: string[]
}> = ({
    lpLink,
    inline,
    initialVariantShopifyId = null,
    bodyBadgesOverride,
}) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { setActiveLPLink } = useContext(PageContext)
    const { HIDE_CART_OVERRIDE } = useContext(QueryParamContext)
    const {
        displayPoweredByFooter,
        secondaryCtaFillColor,
        secondaryCtaTextColor,
    } = useContext(CustomizationContext)
    const displayShopPayButtons = useShopPayButtons()
    const { Analytics } = useContext(AnalyticsContext)
    const { cart } = useContext(CartContext)
    const {
        activeVariant,
        setActiveVariant,
        reviewsRef,
        price,
        comparePrice,
        discountPercentage,
        selectedSellingPlanId,
        setSelectedSellingPlanId,
    } = useProductData(
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        lpLink,
        landingPage,
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        !inline && initialVariantShopifyId
            ? // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
              { initialVariantShopifyId }
            : undefined,
    )

    const variant = useMemo(
        () =>
            buildCartVariant(
                activeVariant,
                lpLink,
                landingPage.discount ?? null,
                !!selectedSellingPlanId,
            ),
        [activeVariant, lpLink, landingPage.discount, selectedSellingPlanId],
    )

    const productContainerRef = useRef<HTMLDivElement>(null)
    const scrollRef = !inline ? productContainerRef : undefined
    const isHeaderVisible = useHeaderScrollVisibility(scrollRef)
    // The modal carries a floating close button instead of a header bar, so
    // the product starts at the top of the panel.
    const desktopModal = useDesktopModal() && !inline
    const benefits = getCartActionBrandBenefits(
        landingPage,
        landingPage.button?.type,
    )
    const benefitsCarouselIndex = useBenefitsCarousel(benefits)

    const onScreen = useOnScreen(productContainerRef)

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (onScreen && inline && activeVariant) {
            Analytics.TRACK_PRODUCT_SCROLL_VIEW_CONTENT(lpLink, activeVariant)
        }
    }, [onScreen, inline, activeVariant, lpLink.linkHash, Analytics])

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!inline && activeVariant) {
            EL_USER_DATA(cart)
        }

        // Tracking stuff
        if (activeVariant) {
            EL_VIEW_ITEM(lpLink, activeVariant)
            AttentiveTracker.trackProductView(lpLink, activeVariant)
            PostscriptTracker.trackProductView(lpLink, activeVariant)
            BlotoutTrackker.trackProductView(lpLink, activeVariant)
            ConvergeTracker.trackProductView(lpLink, activeVariant)
            MetaPixelTracker.trackProductView(
                landingPage.client.clientHash,
                lpLink,
            )
        }
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!inline) {
            Analytics.TRACK_PDP_VARIANT_VIEW(lpLink, activeVariant)
        }
    }, [inline, activeVariant])

    const _onNavGridClose = () => {
        setActiveLPLink(null)
    }

    if (!lpLink) return null

    const hasBrandBenefits = hasStickyFooterBrandBenefits(
        benefits,
        variant.available,
    )

    return (
        <ProductContext.Provider value={{ reviewsRef }}>
            <DrawerContainer
                inline={inline}
                modal={desktopModal}
                ref={productContainerRef}
            >
                {!desktopModal && (
                    <DrawerHeader
                        onClose={_onNavGridClose}
                        rightSlot={
                            !HIDE_CART_OVERRIDE ? <CartIcon /> : undefined
                        }
                        bannerText={landingPage.bannerText}
                        bannerBenefits={landingPage.bannerBenefits}
                        isHeaderVisible={isHeaderVisible}
                    />
                )}
                {!inline && !HIDE_CART_OVERRIDE && (
                    <FloatingCart isVisible={!isHeaderVisible} />
                )}
                <ProductContent
                    lpLink={lpLink}
                    activeVariant={activeVariant}
                    setActiveVariant={setActiveVariant}
                    reviewsRef={reviewsRef}
                    price={price}
                    comparePrice={comparePrice}
                    discountPercentage={discountPercentage}
                    hideCartOverride={HIDE_CART_OVERRIDE}
                    displayPoweredByFooter={displayPoweredByFooter}
                    // The non-inline drawer container is the scroll box, so
                    // the product both skips its own and hands the drawer the
                    // ref to reset on product change.
                    isInOuterScrollContainer={!inline}
                    outerScrollContainerRef={
                        !inline ? productContainerRef : undefined
                    }
                    bodyBadgesOverride={bodyBadgesOverride}
                    selectedSellingPlanId={selectedSellingPlanId}
                    onSellingPlanSelect={setSelectedSellingPlanId}
                />
                <StickyFooterWrapper hasBrandBenefits={hasBrandBenefits}>
                    <FooterContainer
                        style={{
                            padding: STICKY_FOOTER_INNER_PADDING,
                            boxSizing: 'border-box',
                        }}
                    >
                        <div
                            style={{
                                paddingBottom: hasBrandBenefits
                                    ? 0
                                    : STICKY_FOOTER_BUTTON_AREA_PADDING_BOTTOM,
                                width: '100%',
                            }}
                        >
                            {variant.available ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        width: '100%',
                                        gap: STICKY_FOOTER_BUTTON_GAP,
                                    }}
                                >
                                    <ProductDrawerCTA
                                        variant={variant}
                                        sellingPlanId={selectedSellingPlanId}
                                        action={INLINE_CTA_ACTION.ADD_TO_CART}
                                        backgroundColor={secondaryCtaFillColor}
                                        textColor={secondaryCtaTextColor}
                                        border={`var(${SECONDARY_CTA_BORDER_VAR})`}
                                    />
                                    <ProductDrawerCTA
                                        variant={variant}
                                        sellingPlanId={selectedSellingPlanId}
                                        action={INLINE_CTA_ACTION.BUY_NOW}
                                        loader={true}
                                        useShopPay={displayShopPayButtons}
                                    />
                                </div>
                            ) : (
                                <ProductDrawerCTA
                                    variant={variant}
                                    sellingPlanId={selectedSellingPlanId}
                                />
                            )}
                        </div>
                    </FooterContainer>
                    {hasBrandBenefits && (
                        <BenefitsCarouselViewport
                            paddingBottom={
                                STICKY_FOOTER_BENEFITS_CAROUSEL_PADDING_BOTTOM
                            }
                        >
                            {benefits.map((benefit, index) => {
                                const isActive = index === benefitsCarouselIndex
                                return (
                                    <BenefitSlide
                                        key={benefit.id}
                                        isActive={isActive}
                                        delay={isActive ? 0.3 : 0}
                                        aria-hidden={!isActive}
                                    >
                                        <BenefitSlideContent
                                            benefit={benefit}
                                            showIcon={true}
                                            iconSize={22}
                                        />
                                    </BenefitSlide>
                                )
                            })}
                        </BenefitsCarouselViewport>
                    )}
                </StickyFooterWrapper>
            </DrawerContainer>
        </ProductContext.Provider>
    )
}

const ProductDrawerOverlay = styled(FullScreenContainer)({
    background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    pointerEvents: 'none',
    display: 'flex',
    [ScrollingLayout.DESKTOP_MEDIA]: overlayModalScrim,
})

/**
 * Declares the section container the product reads to lay itself out in two
 * columns, exactly as it does in an inline PDP section, and becomes the modal
 * panel once there is room for one. Everything inside sizes against this
 * element, so the header and checkout bars span it rather than the window.
 */
const OverlaySurface = styled.div({
    width: '100%',
    height: '100%',
    containerType: 'inline-size',
    containerName: SECTION_CONTAINER_NAME,
    // Anchors the floating close button to the panel rather than to the
    // content, which scrolls underneath it.
    position: 'relative',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        ...overlayModalPanel,
        // The panel hugs its content, so the scroll box below it has to be the
        // part that gives when the content outgrows the viewport.
        display: 'flex',
        flexDirection: 'column',
    },
})

export const ProductDrawer: React.FC<{ zIndex?: number }> = ({
    zIndex = 'unset',
}) => {
    const {
        activeLPLink,
        setActiveLPLink,
        activeLPLinkInitialVariantShopifyId,
        activeLPLinkBodyBadgesOverride,
        isCartOpen,
        lastOpenedOverlay,
    } = useContext(PageContext)
    const { landingPage } = useContext(ViddyLPContext)
    const fullLink = useFullLandingPageLink(activeLPLink)

    const close = useCallback(() => setActiveLPLink(null), [setActiveLPLink])
    const desktopModal = useDesktopModal()
    // Mirrors how the two are stacked: the cart only sits above the product
    // when it was the more recent of the two to open.
    const isTopmost =
        activeLPLink !== null && !(isCartOpen && lastOpenedOverlay === 'cart')
    useEscapeToDismiss(isTopmost, close)

    // Track recently viewed when product drawer opens
    useEffect(() => {
        if (activeLPLink) {
            addRecentlyViewedToSession(
                landingPage.landingPageHash,
                activeLPLink,
            )
        }
    }, [activeLPLink, landingPage.landingPageHash])

    if (activeLPLink == null) return null

    const product = fullLink ? (
        <ProductPage
            key={fullLink.linkHash}
            lpLink={fullLink}
            inline={false}
            initialVariantShopifyId={activeLPLinkInitialVariantShopifyId}
            bodyBadgesOverride={activeLPLinkBodyBadgesOverride}
        />
    ) : null

    return (
        <ProductDrawerOverlay
            style={{ zIndex }}
            onClick={(event) => {
                if (event.target === event.currentTarget) close()
            }}
        >
            <OverlaySurface>
                {desktopModal && <OverlayCloseButton onClick={close} />}
                {product}
            </OverlaySurface>
        </ProductDrawerOverlay>
    )
}
